import { useState } from 'react';
import { Users, Package, MapPin, SlidersHorizontal, KeyRound, ScrollText, ListChecks, PiggyBank, Presentation } from 'lucide-react';
import DemoBankEditor from './DemoBankEditor';
import SettingsEditor from './SettingsEditor';
import ModelCosts from './ModelCosts';
import SecretsEditor from './SecretsEditor';
import RecordEditor, { INFLUENCER_FIELDS, PRODUCT_FIELDS, LOCATION_FIELDS } from './RecordEditor';
import RunHistory from './RunHistory';
import QueueInspector from './QueueInspector';

type Section =
  | 'settings'
  | 'stats'
  | 'influencers'
  | 'products'
  | 'locations'
  | 'secrets'
  | 'runs'
  | 'queue'
  | 'demo';

const SECTIONS: Array<{ id: Section; label: string; icon: typeof Users }> = [
  { id: 'settings', label: 'Settings & prompts', icon: SlidersHorizontal },
  { id: 'stats', label: 'Model costs', icon: PiggyBank },
  { id: 'influencers', label: 'Influencers', icon: Users },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'locations', label: 'Locations', icon: MapPin },
  { id: 'secrets', label: 'API keys', icon: KeyRound },
  { id: 'runs', label: 'Run history', icon: ScrollText },
  { id: 'queue', label: 'Queue', icon: ListChecks },
  { id: 'demo', label: 'Demo assets', icon: Presentation },
];

/**
 * Everything that used to live in a Google Sheet row or be hardcoded inside an
 * n8n node. Access is enforced by PocketBase collection rules, not by hiding
 * this tab — v1's admin check trusted a self-asserted `x-user-email` header.
 */
export default function AdminPanel() {
  const [section, setSection] = useState<Section>('settings');

  return (
    <div className="grid md:grid-cols-5 gap-6 items-start">
      <nav className="zen-card p-3 md:col-span-1 flex md:flex-col gap-1 overflow-x-auto">
        {SECTIONS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSection(id)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold text-left
                        whitespace-nowrap transition-colors cursor-pointer ${
                          section === id
                            ? 'bg-natural-sage/10 text-natural-sage'
                            : 'text-natural-text/60 hover:bg-natural-bg hover:text-natural-text'
                        }`}
          >
            <Icon size={14} className="shrink-0" />
            {label}
          </button>
        ))}
      </nav>

      <div className="md:col-span-4">
        {section === 'settings' && <SettingsEditor />}
        {section === 'stats' && <ModelCosts />}
        {section === 'secrets' && <SecretsEditor />}
        {section === 'runs' && <RunHistory />}
        {section === 'queue' && <QueueInspector />}
        {section === 'demo' && <DemoBankEditor />}

        {section === 'influencers' && (
          <RecordEditor
            collection="influencers"
            title="Influencers"
            description="The character, their locks, their prompt, and who is allowed to use them."
            sort="name"
            fields={INFLUENCER_FIELDS}
            labelField="name"
          />
        )}

        {section === 'products' && (
          <RecordEditor
            collection="products"
            title="Products"
            description="Guidelines here are included in the script prompt."
            sort="name"
            fields={PRODUCT_FIELDS}
            labelField="name"
          />
        )}

        {section === 'locations' && (
          <RecordEditor
            collection="locations"
            title="Locations"
            description="Reference images per scene tag. A script's location_tag must match one of these."
            sort="sort"
            fields={LOCATION_FIELDS}
            labelField="name"
          />
        )}
      </div>
    </div>
  );
}
